import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FundingProgress } from '../../../modules/labraChat/reducer'
import { useStyles } from './FundingProgressCard.styles'

const PERCENT_MAX = 100

/** Short name for MPOPP and POC. Every other program uses its catalog name. */
const programLabel = (program: string): string => {
  switch (program.toLowerCase()) {
    case 'poc':
      return 'PoC'
    case 'mpopp':
      return 'MPOPP'
    default:
      return program.trim() || 'AWS'
  }
}

interface FundingProgressCardProps {
  progress: FundingProgress
}

/**
 * Determinate stepper card for a funding run, driven entirely by the
 * `funding_plan`/`funding_step` SSE frames captured on the assistant message.
 * Step order and labels come from the server; nothing here is hardcoded.
 * `resume_poc_run` submits without replaying stages, so it renders an
 * indeterminate "Submitting…" state instead of the stepper.
 */
const FundingProgressCardComponent: React.FC<FundingProgressCardProps> = ({
  progress,
}) => {
  const { classes, cx } = useStyles()
  const { steps, indeterminate, ended, opportunityName } = progress

  const total = steps.length
  const completed = steps.filter(s => s.status === 'done').length
  const failed = steps.filter(s => s.status === 'failed').length
  const pct = total ? Math.round((completed / total) * PERCENT_MAX) : 0

  // resume_poc_run never fills the stepper; show an indeterminate submit state
  // until the run ends, then fall through to whatever final step state arrived.
  const showSubmitting = indeterminate && !ended
  // Shimmer the determinate fill only while the run is still in flight.
  const isActive = !ended && !showSubmitting

  return (
    <div className={classes.card}>
      <div className={classes.body}>
        <div className={classes.header}>
          <div>
            <div className={classes.title}>
              {programLabel(progress.program)} funding progress
            </div>
            {opportunityName && (
              <div className={classes.subtitle}>
                Opportunity {opportunityName}
              </div>
            )}
          </div>
          <div className={classes.pctGroup}>
            <div className={classes.pct}>{pct}%</div>
            <div className={classes.pctCaption}>complete</div>
          </div>
        </div>

        <div className={classes.track}>
          <div
            className={cx(
              classes.fill,
              showSubmitting && classes.fillIndeterminate,
              isActive && classes.fillActive
            )}
            style={showSubmitting ? undefined : { width: `${pct}%` }}
          />
        </div>
        <div className={classes.completedLabel}>
          {showSubmitting
            ? 'Submitting…'
            : failed
              ? `${completed} of ${total} stages complete, ${failed} failed`
              : `${completed} of ${total} stages complete`}
        </div>

        {showSubmitting ? (
          <div className={classes.submittingNote}>
            Submitting your funding application to AWS…
          </div>
        ) : (
          <>
            <div className={classes.divider} />
            <div>
              {steps.map((item, idx) => {
                const isDone = item.status === 'done'
                const isCurrent = item.status === 'running'
                const isFailed = item.status === 'failed'
                const showConnector = idx < total - 1
                return (
                  <div
                    key={item.step}
                    className={cx(
                      classes.stepRow,
                      isCurrent && classes.stepRowCurrent
                    )}
                  >
                    <div className={classes.railColumn}>
                      {showConnector && (
                        <div
                          className={cx(
                            classes.connector,
                            isDone
                              ? classes.connectorDone
                              : classes.connectorPending
                          )}
                        />
                      )}
                      <div
                        className={cx(
                          classes.circle,
                          isDone && classes.circleDone,
                          isCurrent && classes.circleCurrent,
                          isFailed && classes.circleFailed,
                          !isDone &&
                            !isCurrent &&
                            !isFailed &&
                            classes.circlePending
                        )}
                      >
                        {isDone ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className={classes.checkIcon}
                          />
                        ) : isFailed ? (
                          <FontAwesomeIcon
                            icon={faXmark}
                            className={classes.failedIcon}
                          />
                        ) : (
                          <span
                            className={cx(
                              classes.circleNum,
                              isCurrent && classes.circleNumCurrent
                            )}
                          >
                            {idx + 1}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={classes.stepTextCol}>
                      <div
                        className={cx(
                          classes.stepName,
                          isDone && classes.stepNameDone,
                          isCurrent && classes.stepNameActive
                        )}
                      >
                        {item.label}
                      </div>
                      {isCurrent && item.detail && (
                        <div className={classes.stepDetail}>{item.detail}</div>
                      )}
                    </div>
                    {isCurrent && (
                      <div className={classes.inProgressPill}>In progress</div>
                    )}
                    {isDone && <div className={classes.doneLabel}>Done</div>}
                    {isFailed && (
                      <div className={classes.failedLabel}>Failed</div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const FundingProgressCard = React.memo(FundingProgressCardComponent)
